import { v4 as uuidv4 } from "uuid";
import { createEmptyGroup } from "../../../../../components/queryBuilder/queryBuilderUtils";
import type { QueryBuilderValue } from "../../../../../data-model/queryBuilder";
import {
  sanitizeTriggerForFirestore,
  type CallingFirestoreRule,
  type CallingRuleAction,
  type CallingRuleState,
  type CallingRuleTrigger,
} from "../../callingConfiguration/callingRuleUtils";
import type { FirstStpConfigBlock, FirstStpConfigStep } from "./firstStpLogicUtils";


const NO_TOUCH_CONFIG_DOC_ID = "latest";

/** Firestore document at `api_config/{orgId}/no-touch-config/{latest|second_stp_review|agentic}`. */
export interface FirstStpReviewFirestoreDoc {
  id?: string;
  rules?: CallingFirestoreRule[];
  condition_blocks?: FirstStpConfigBlock[] | Record<string, FirstStpConfigBlock>;
  triggers?: CallingRuleTrigger[];
  /** Document-level gate; currently edited on the Second STP Review tab. */
  enabled?: boolean;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
}

export type FirstStpReviewRuleState = CallingRuleState;

/** Block metadata used to group flat UI rules and rebuild condition_blocks on save. */
export interface FirstStpConditionBlockLayout {
  lable: string;
  subtext?: string;
  active?: boolean;
  ruleIds: string[];
}

type RawConditionBlock = FirstStpConfigBlock & {
  label?: string;
  rules?: FirstStpConfigStep[];
};

/** Normalizes a query-builder tree for the UI (ids + string values). */
function normalizeConditionForQueryBuilder(
  condition: QueryBuilderValue,
): QueryBuilderValue {
  return {
    ...condition,
    id: condition.id ?? uuidv4(),
    conjunction: condition.conjunction ?? "AND",
    children: (condition.children ?? []).map((child) =>
      child.type === "condition"
        ? {
            ...child,
            id: child.id ?? uuidv4(),
            value:
              typeof child.value === "number"
                ? String(child.value)
                : Array.isArray(child.value)
                  ? child.value.map((v) =>
                      typeof v === "number" ? String(v) : v,
                    )
                  : (child.value ?? ""),
          }
        : normalizeConditionForQueryBuilder(child),
    ),
  } as QueryBuilderValue;
}

/** Returns true when a query-builder group has at least one child node. */
function hasQueryContent(condition: QueryBuilderValue | undefined): boolean {
  return (condition?.children ?? []).length > 0;
}

/** Normalizes block/step labels for case-insensitive matching. */
function normalizeLabelKey(label: string | undefined): string {
  return (label ?? "").trim().toLowerCase();
}

/** Builds default review actions for a step label when loading condition_blocks. */
function buildDefaultActions(label: string): CallingRuleAction[] {
  const trimmed = label.trim() || "Rule";
  return [
    { type: "set", field: "needs_review", value: "Needed" },
    { type: "set", field: "review_reason", value: `${trimmed} Failed` },
  ];
}

/**
 * Resolves review actions from a persisted step, optional flat-rule fallback, or defaults.
 *
 * @param step - Condition block step from Firestore
 * @param label - Step label used for default action text
 * @param fallbackActions - Actions from a matching flat rule (legacy docs)
 * @return CallingRuleAction[] - Actions for the UI rule state
 */
function resolveStepActions(
  step: FirstStpConfigStep,
  label: string,
  fallbackActions?: CallingRuleAction[],
): CallingRuleAction[] {
  if ((step.actions ?? []).length > 0) {
    return step.actions!.map((action) => ({ ...action }));
  }
  if ((fallbackActions ?? []).length > 0) {
    return fallbackActions!.map((action) => ({ ...action }));
  }
  return buildDefaultActions(label);
}

/**
 * Coerces Firestore condition_blocks into an array (handles map-shaped legacy data).
 *
 * @input raw - condition_blocks field from Firestore
 * @return FirstStpConfigBlock[] - Normalized block list
 */
function normalizeConditionBlocks(
  raw: FirstStpReviewFirestoreDoc["condition_blocks"],
): RawConditionBlock[] {
  if (raw == null) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw as RawConditionBlock[];
  }
  if (typeof raw === "object") {
    return Object.values(raw) as RawConditionBlock[];
  }
  return [];
}

/**
 * Reads steps from a condition block (`steps` or legacy `rules` key).
 *
 * @input block - Raw condition block from Firestore
 * @return FirstStpConfigStep[] - Steps sorted by priority
 */
function getBlockSteps(block: RawConditionBlock): FirstStpConfigStep[] {
  const steps = block.steps ?? block.rules ?? [];
  return [...steps].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
}

/**
 * Resolves the block title from Firestore (`lable` typo or `label`).
 *
 * @input block - Raw condition block from Firestore
 * @return string - Block title
 */
function getBlockLabel(block: RawConditionBlock): string {
  return block.lable ?? block.label ?? "";
}

/**
 * Maps flat Firestore rules[] entries to editable UI rule state.
 *
 * @input doc - Firestore document with optional flat rules
 * @return FirstStpReviewRuleState[] - Parsed flat rules with actions preserved
 */
function mapFlatFirestoreRules(
  doc: FirstStpReviewFirestoreDoc,
): FirstStpReviewRuleState[] {
  return (doc.rules ?? []).map((rule, index) => {
    const conditionKey: "rule" | "when" =
      rule.rule != null ? "rule" : rule.when != null ? "when" : "rule";
    const condition = rule.rule ?? rule.when ?? createEmptyGroup();
    const label = rule.label ?? `Rule ${index + 1}`;
    return {
      id: `first-stp-review-rule-${index}-${uuidv4()}`,
      label,
      subtext: rule.subtext,
      active: rule.active ?? true,
      actions:
        (rule.actions ?? []).length > 0
          ? rule.actions!.map((action) => ({ ...action }))
          : buildDefaultActions(label),
      condition: normalizeConditionForQueryBuilder(condition),
      conditionKey,
    };
  });
}

/**
 * Maps a condition_blocks step to editable first-STP review rule state.
 *
 * @input step - Step from a condition block
 * @input index - Global step index for fallback labeling
 * @input flatRule - Optional matching flat rule for actions/conditions
 * @return FirstStpReviewRuleState - UI rule state
 */
function mapStepToRuleState(
  step: FirstStpConfigStep,
  index: number,
  flatRule?: FirstStpReviewRuleState,
): FirstStpReviewRuleState {
  const stepCondition = normalizeConditionForQueryBuilder(
    step.rule ?? createEmptyGroup(),
  );
  const label = step.label ?? flatRule?.label ?? `Rule ${index + 1}`;

  if (flatRule != null) {
    return {
      ...flatRule,
      id: step.id || flatRule.id,
      label,
      subtext: step.subtext ?? flatRule.subtext,
      active: step.active ?? flatRule.active,
      condition: hasQueryContent(stepCondition)
        ? stepCondition
        : flatRule.condition,
      actions: resolveStepActions(step, label, flatRule.actions),
      conditionKey: flatRule.conditionKey,
    };
  }

  return {
    id: step.id || `first-stp-review-rule-${uuidv4()}`,
    label,
    subtext: step.subtext,
    active: step.active ?? true,
    actions: resolveStepActions(step, label),
    condition: stepCondition,
    conditionKey: "rule",
  };
}

/**
 * Wraps flat UI rules in a single default condition_blocks entry (legacy docs).
 *
 * @input rules - Flat rules from a legacy document
 * @return FirstStpConditionBlockLayout[] - One block containing all rule ids
 */
function wrapRulesInDefaultConditionBlock(
  rules: FirstStpReviewRuleState[],
): FirstStpConditionBlockLayout[] {
  if (rules.length === 0) {
    return [];
  }
  return [
    {
      lable: "Condition 1",
      subtext: "",
      active: true,
      ruleIds: rules.map((rule) => rule.id),
    },
  ];
}

/**
 * Builds UI state from condition_blocks, merging flat rules[] when steps lack conditions.
 *
 * @input blocks - Normalized condition blocks from Firestore
 * @input flatRules - Flat rules from the same document
 * @return Parsed rules and block layout
 */
function parseConditionBlocksWithFlatRules(
  blocks: RawConditionBlock[],
  flatRules: FirstStpReviewRuleState[],
): {
  rules: FirstStpReviewRuleState[];
  blockLayout: FirstStpConditionBlockLayout[];
} {
  const flatByLabel = new Map<string, FirstStpReviewRuleState>();
  for (const rule of flatRules) {
    flatByLabel.set(normalizeLabelKey(rule.label), rule);
  }

  const rules: FirstStpReviewRuleState[] = [];
  const blockLayout: FirstStpConditionBlockLayout[] = [];
  const usedFlatRuleIds = new Set<string>();
  let globalIndex = 0;

  for (const block of blocks) {
    const steps = getBlockSteps(block);
    const ruleIds: string[] = [];

    for (const step of steps) {
      const flatMatch = flatByLabel.get(normalizeLabelKey(step.label));
      const rule = mapStepToRuleState(step, globalIndex, flatMatch);
      globalIndex += 1;
      rules.push(rule);
      ruleIds.push(rule.id);
      if (flatMatch != null) {
        usedFlatRuleIds.add(flatMatch.id);
      }
    }

    blockLayout.push({
      lable: getBlockLabel(block),
      subtext: block.subtext,
      active: block.active ?? true,
      ruleIds,
    });
  }

  if (rules.length === 0 && flatRules.length > 0 && blocks.length > 0) {
    const allRuleIds = flatRules.map((rule) => rule.id);
    return {
      rules: flatRules,
      blockLayout: blocks.map((block, blockIndex) => ({
        lable: getBlockLabel(block) || `Condition ${blockIndex + 1}`,
        subtext: block.subtext,
        active: block.active ?? true,
        ruleIds: blockIndex === 0 ? allRuleIds : [],
      })),
    };
  }

  const unmatchedFlatRules = flatRules.filter(
    (rule) => !usedFlatRuleIds.has(rule.id),
  );
  if (unmatchedFlatRules.length > 0 && blockLayout.length > 0) {
    const targetBlock = blockLayout[blockLayout.length - 1];
    for (const rule of unmatchedFlatRules) {
      rules.push(rule);
      targetBlock.ruleIds.push(rule.id);
    }
  }

  return { rules, blockLayout };
}

/**
 * Parses a first STP review Firestore document into UI rules and condition_blocks layout.
 * Merges condition_blocks steps with flat rules[] when both are present.
 *
 * @input doc - Firestore document from first_stp_review
 * @return Parsed rules and block layout derived from condition_blocks
 */
export function parseFirstStpReviewDoc(doc: FirstStpReviewFirestoreDoc): {
  rules: FirstStpReviewRuleState[];
  blockLayout: FirstStpConditionBlockLayout[];
  usesConditionBlocks: boolean;
} {
  const flatRules = mapFlatFirestoreRules(doc);
  const blocks = normalizeConditionBlocks(doc.condition_blocks);

  if (blocks.length > 0) {
    const parsed = parseConditionBlocksWithFlatRules(blocks, flatRules);
    if (parsed.rules.length > 0 || parsed.blockLayout.length > 0) {
      return {
        rules: parsed.rules,
        blockLayout: parsed.blockLayout,
        usesConditionBlocks: true,
      };
    }
  }

  if (flatRules.length > 0) {
    return {
      rules: flatRules,
      blockLayout: wrapRulesInDefaultConditionBlock(flatRules),
      usesConditionBlocks: true,
    };
  }

  return { rules: [], blockLayout: [], usesConditionBlocks: true };
}

/**
 * Groups flat UI rules under their condition block layout entries.
 *
 * @input blockLayout - Block layout from parseFirstStpReviewDoc
 * @input rules - Current editable rules
 * @return Block groups with resolved rule states
 */
export function groupRulesByConditionBlockLayout(
  blockLayout: FirstStpConditionBlockLayout[],
  rules: FirstStpReviewRuleState[],
): {
  layout: FirstStpConditionBlockLayout;
  rules: FirstStpReviewRuleState[];
}[] {
  const ruleById = new Map(rules.map((rule) => [rule.id, rule]));
  return blockLayout.map((layout) => ({
    layout,
    rules: layout.ruleIds
      .map((ruleId) => ruleById.get(ruleId))
      .filter((rule): rule is FirstStpReviewRuleState => rule != null),
  }));
}

/**
 * Builds a Firestore payload using condition_blocks (no flat rules array).
 *
 * @input rules - Current UI rules
 * @input blockLayout - Block grouping for steps
 * @input triggers - Document triggers
 * @input existingDoc - Prior Firestore document for metadata
 * @input updatedBy - User email for updated_by
 * @return FirstStpReviewRuleDoc - Payload for setDocument
 */
export function buildFirstStpReviewConditionBlocksPayload(
  rules: FirstStpReviewRuleState[],
  blockLayout: FirstStpConditionBlockLayout[],
  triggers: CallingRuleTrigger[],
  existingDoc: FirstStpReviewFirestoreDoc | null,
  updatedBy?: string | null,
): FirstStpReviewFirestoreDoc {
  const ruleById = new Map(rules.map((rule) => [rule.id, rule]));
  const nowIso = new Date().toISOString();

  const condition_blocks: FirstStpConfigBlock[] = blockLayout.map(
    (block, blockIndex) => ({
      lable: block.lable || `Condition ${blockIndex + 1}`,
      subtext: block.subtext,
      active: block.active ?? true,
      steps: block.ruleIds.map((ruleId, stepIndex) => {
        const rule = ruleById.get(ruleId);
        const label = rule?.label ?? `Step ${stepIndex + 1}`;
        return {
          id: ruleId,
          priority: stepIndex + 1,
          label,
          subtext: rule?.subtext ?? "",
          active: rule?.active ?? true,
          rule: rule?.condition ?? createEmptyGroup(),
          actions:
            rule != null && rule.actions.length > 0
              ? rule.actions.map((action) => ({ ...action }))
              : buildDefaultActions(label),
        };
      }),
    }),
  );

  return {
    id: existingDoc?.id ?? NO_TOUCH_CONFIG_DOC_ID,
    triggers: triggers.map(sanitizeTriggerForFirestore),
    condition_blocks,
    ...(existingDoc?.created_at != null && {
      created_at: existingDoc.created_at,
    }),
    ...(existingDoc?.created_by != null && {
      created_by: existingDoc.created_by,
    }),
    updated_at: nowIso,
    ...(updatedBy != null && updatedBy !== "" && { updated_by: updatedBy }),
  };
}
